import {
  Button,
  ButtonGroup,
  fieldKey,
  Fieldset,
  Form,
  TextInput,
  useControlled,
  H1,
} from "@datapay-by-datacom/design-system";
import { WorkspaceScreen } from "../../workspace/components/WorkspaceScreen";
import { useProvisionerCheck } from "../hooks/useProvisionerCheck";
import { useExportThreadsCsv } from "../hooks/useExportThreadsCsv";
import { UnauthorizedMessage } from "../../shared/components";
import styles from "./ThreadExportScreen.module.css";

interface ExportFormData {
  pageSize: string;
  fromDate: string;
  toDate: string;
}

const DEFAULT_PAGE_SIZE = 100;

export function ThreadExportScreen() {
  const { isProvisioner } = useProvisionerCheck();
  const exportMutation = useExportThreadsCsv();

  const field = useControlled<ExportFormData>({
    pageSize: "100",
    fromDate: "",
    toDate: "",
  });

  const input = fieldKey(field);

  if (!isProvisioner()) {
    return (
      <WorkspaceScreen>
        <UnauthorizedMessage />
      </WorkspaceScreen>
    );
  }

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    const params = {
      page_size: parseInt(field.value.pageSize) || DEFAULT_PAGE_SIZE,
      from_created_at: field.value.fromDate || null,
      to_created_at: field.value.toDate || null,
    };

    // Remove null/undefined values
    const cleanParams = Object.fromEntries(
      Object.entries(params).filter(
        ([_, value]) => value !== null && value !== undefined && value !== ""
      )
    );

    exportMutation.mutate(cleanParams);
  };

  return (
    <WorkspaceScreen>
      <div className={styles.container}>
        <div className={styles.header}>
          <H1>Thread Export</H1>
          <p>Export all threads across workspaces as a CSV file</p>
        </div>

        <Form onSubmit={handleSubmit}>
          <Fieldset>
            <TextInput
              label="Page Size"
              type="number"
              placeholder="Number of threads per page (default: 100)"
              description="Number of threads to fetch per page during export (1-1000)"
              {...input("pageSize")}
            />

            <TextInput
              label="From Date"
              type="datetime-local"
              description="Filter threads created after this date (optional)"
              {...input("fromDate")}
            />

            <TextInput
              label="To Date"
              type="datetime-local"
              description="Filter threads created before this date (optional)"
              {...input("toDate")}
            />

            <ButtonGroup align="right">
              <Button
                type="submit"
                variant="primary"
                isDisabled={exportMutation.isPending}
              >
                {exportMutation.isPending ? "Exporting..." : "Export CSV"}
              </Button>
            </ButtonGroup>
          </Fieldset>
        </Form>

        {exportMutation.isError && (
          <div className={styles.error}>
            <p>
              Error:{" "}
              {exportMutation.error?.message || "Failed to export threads"}
            </p>
          </div>
        )}

        {exportMutation.isSuccess && (
          <div className={styles.success}>
            <p>
              Threads exported successfully! Download should start
              automatically.
            </p>
          </div>
        )}
      </div>
    </WorkspaceScreen>
  );
}
